// Live's caption words (#239, spec docs/specs/editor/captions.md): the shapes the importers, the
// segmenter, the Transcript and the engine share, and a caption's text from its words. Pure.

/** A word on the video's time, as a subtitle file or a transcript gives it. */
export interface TimedWord {
  text: string;
  /** From the video's start, in whole milliseconds. */
  startMs: number;
  endMs: number;
  /** A transcript's mark (#264); none from a subtitle file. */
  emphasis?: "positive" | "negative" | null;
}

/** A caption's word (the contract's `CaptionWord`): timed from the caption's start, on its line. */
export interface CaptionWord {
  text: string;
  /** From the caption's start, in whole milliseconds. */
  startMs: number;
  endMs: number;
  /** Its line in the caption, 0 first; a line no word is on is drawn empty. */
  line: number;
  /** Live's emphasis (#264): a transcript marks a word positive or negative; a file never does. */
  emphasis: "positive" | "negative" | null;
}

/** A caption's words at most, its lines at most (the contract's bounds). */
export const MAX_CAPTION_WORDS = 100;
export const MAX_CAPTION_LINES = 10;
/** A video's captions at most, and their words in all. */
export const MAX_CAPTIONS = 1000;
export const MAX_TIMELINE_CAPTION_WORDS = 5000;
/** A word's characters at most. */
export const MAX_WORD_CHARS = 100;
/** A video's longest: no caption word starts at or after it. */
export const MAX_VIDEO_MS = 300_000;

/** Where a caption sits on the frame, in live's pixels: its lines are drawn centred on it. */
export interface CaptionBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** A word cut to the contract's length, by characters as the server counts them. */
export function cutWord(text: string): string {
  return Array.from(text).slice(0, MAX_WORD_CHARS).join("");
}

/** A caption's lines, each its words, the empty ones too (live keeps a blank line). */
export function linesOf(words: readonly CaptionWord[]): CaptionWord[][] {
  const last = words.reduce((most, word) => Math.max(most, word.line), -1);
  const lines: CaptionWord[][] = Array.from({ length: last + 1 }, () => []);
  for (const word of words) lines[word.line]!.push(word);
  return lines;
}

/** A caption's text as live's Transcript shows it: a line's words joined by spaces, lines by line
 * breaks. */
export function captionText(words: readonly CaptionWord[]): string {
  return linesOf(words)
    .map((line) => line.map((word) => word.text).join(" "))
    .join("\n");
}

/**
 * The characters the server counts as blank (`platform/contracts.py`'s BLANK): JavaScript's `\s`
 * misses U+0085, which the server's `^\S+$` refuses inside a word.
 */
const BLANK = "\\t\\n\\v\\f\\r \\u0085\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000\\ufeff";
const BLANKS = new RegExp(`[${BLANK}]+`, "gu");
const NOT_BLANKS = new RegExp(`[^${BLANK}]+`, "gu");
/** Control characters: never in a word (the server refuses U+0000 anywhere). */
const CONTROLS = /\p{Cc}/gu;

/** A run of a text's characters as a word: its control characters dropped. */
export function cleanWord(text: string): string {
  return text.replace(CONTROLS, "");
}

/** A text's words as the server reads them: split on its blank characters, each word without
 * control characters, none empty. */
export function wordsIn(text: string): string[] {
  return text
    .split(BLANKS)
    .map(cleanWord)
    .filter((word) => word !== "");
}

/** A text's words with where each starts in it (a run of only control characters is none). */
export function wordRuns(text: string): { at: number; end: number; text: string }[] {
  return [...text.matchAll(NOT_BLANKS)]
    .map((match) => ({ at: match.index, end: match.index + match[0].length, text: cleanWord(match[0]) }))
    .filter((run) => run.text !== "");
}

/** A text with every run of blank characters one space, and no control characters. */
export function plainText(text: string): string {
  return wordsIn(text).join(" ");
}
